import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import { useQuery } from "@tanstack/react-query";
import { ATKOMST_URL, FIXA_SERVICE_NAME } from "../../shared/constants/atkomst";

type AtkomstPermission = {
  id: number;
  name: string;
  description: string;
  service: string;
};

type AtkomstRole = {
  id: number;
  type: string;
  clientId: number;
  clientReference: string;
  clientName: string;
  clientHasChildren: boolean;
  isMember: boolean;
  canAssign: boolean;
  canUnassign: boolean;
  clientPath: string[];
  service: string;
  permissions: AtkomstPermission[];
  name: string;
  clientHierarchyType: string;
  description: string;
};

type UseAtkomstRolesParams = {
  email: string;
  storeId: string | undefined;
  storeCountryId: string | undefined;
};

const getRoleNamesFromResponse = (
  body: AtkomstRolesResponse | undefined,
  { storeId, storeCountryId }: Omit<UseAtkomstRolesParams, "email">
) => {
  if (!body) {
    return [];
  }

  const clientReferences = new Set([
    "PLATFORM_OWNER",
    `STO.${storeId}`,
    ...(storeCountryId ? [`RU.${storeCountryId}`] : []),
  ]);

  const roles = body.results
    .filter(
      ({ service, clientReference }) =>
        service === FIXA_SERVICE_NAME && clientReferences.has(clientReference)
    )
    .map(({ name }) => name);

  return Array.from(new Set(roles));
};

type AtkomstRolesResponse = { results: AtkomstRole[]; total: number };
const getAtkomstRoles = async ({
  email,
  storeId,
  storeCountryId,
}: UseAtkomstRolesParams) => {
  const token = await getTokenAsync();

  const query = new URLSearchParams({
    service: FIXA_SERVICE_NAME,
    includeManaged: "false",
    username: email,
    hierarchyTypes: "ORGANIZATIONAL",
  });

  const response = await fetch(`${ATKOMST_URL}/roles?${query.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const body: AtkomstRolesResponse | undefined = await response.json();
  return getRoleNamesFromResponse(body, { storeId, storeCountryId });
};

type UseAtkomstRolesOptions = {
  enabled?: boolean;
};

export const useAtkomstRoles = (
  { email, storeId, storeCountryId }: UseAtkomstRolesParams,
  { enabled = true }: UseAtkomstRolesOptions = {}
) =>
  useQuery<string[]>({
    queryKey: ["atkomstRoles", email, storeId, storeCountryId],
    queryFn: async () =>
      await getAtkomstRoles({ email, storeId, storeCountryId }),
    enabled: enabled && Boolean(email),
    retry: false,
    staleTime: INTERVAL.HOUR,
  });
